// Distribution / Этап 5 — публичная страница приёма приглашения.
// URL: /invite/{token}. Эксперт без аккаунта переходит по ссылке от куратора,
// видит свой email, вводит ФИО и пароль, попадает в систему.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';
import { Button } from '@/components/Button';

interface InviteInfo {
  email: string;
  expires_at: string;
  is_active: boolean;
}

export default function AcceptInvite() {
  const { token = '' } = useParams<{ token: string }>();
  const nav = useNavigate();

  const infoQ = useQuery<InviteInfo>({
    queryKey: ['invite', token],
    queryFn: () => api.get<InviteInfo>(`/invites/${token}`),
    retry: false,
  });

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const accept = useMutation({
    mutationFn: () =>
      api.post<{ accepted: boolean }>(`/invites/${token}/accept`, {
        full_name: fullName.trim(),
        password,
      }),
    onSuccess: () => {
      // Перенаправляем на логин с предзаполненным email
      nav('/login?invited=1', { replace: true });
    },
    onError: (e: unknown) => setError((e as Error).message),
  });

  useEffect(() => { setError(''); }, [fullName, password]);

  if (infoQ.isLoading) {
    return <Centered>Проверяем приглашение…</Centered>;
  }
  if (infoQ.isError) {
    return (
      <Centered>
        <AlertTriangle size={32} color="#DC2626" style={{ margin: '0 auto 12px' }} />
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#DC2626' }}>Приглашение не найдено</h2>
        <p style={{ fontSize: 14, color: '#64748B', marginTop: 8 }}>
          Возможно, ссылка устарела или уже использована. Обратитесь к куратору экспертизы.
        </p>
      </Centered>
    );
  }
  const info = infoQ.data!;

  return (
    <Centered>
      <CheckCircle2 size={32} color="#16A34A" style={{ margin: '0 auto 12px' }} />
      <h2 style={{ fontSize: 20, fontWeight: 600 }}>Принять приглашение</h2>
      <p style={{ fontSize: 14, color: '#475569', marginTop: 8 }}>
        Куратор пригласил вас в систему Коллегиум как эксперта.
      </p>
      <div style={{ marginTop: 24, textAlign: 'left' }}>
        <Field label="Email">
          <input
            value={info.email}
            disabled
            style={inputStyle}
          />
        </Field>
        <Field label="ФИО (как будет отображаться в системе)">
          <input
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="Иванов Иван Иванович"
            style={inputStyle}
          />
        </Field>
        <Field label="Придумайте пароль (минимум 6 символов)">
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={inputStyle}
          />
        </Field>
        {error && (
          <div style={{ padding: 8, background: '#FEE2E2', borderRadius: 6, color: '#991B1B', fontSize: 13, marginTop: 8 }}>
            {error}
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <Button
            variant="primary"
            onClick={() => accept.mutate()}
            disabled={!fullName.trim() || password.length < 6 || accept.isPending}
          >
            {accept.isPending ? 'Создаём аккаунт…' : 'Принять и войти'}
          </Button>
        </div>
      </div>
    </Centered>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #CBD5E1',
  borderRadius: 6,
  fontSize: 14,
};

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 480, width: '100%', background: 'white', padding: 32, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        {children}
      </div>
    </div>
  );
}
